import type { ComponentProps } from 'react';
import React, { useState } from 'react';
import styled from '@emotion/styled';
import type {
  DisplayProps,
  LeftProps,
  OverflowProps,
  PositionProps,
  TopProps,
  WidthProps,
} from 'styled-system';
import { display, left, overflow, position, top, width } from 'styled-system';

import type { CursorProps, VisibilityProps } from 'common/theme/system-utilities';
import { cursor, visibility } from 'common/theme/system-utilities';

export interface ReactRatingProps {
  initialRating?: number;
  readonly?: boolean;
  onChange?: (value: number) => void;
  emptySymbol?: React.ReactNode | React.ReactNode[];
  fullSymbol?: React.ReactNode | React.ReactNode[];
  className?: string;
  ref?: React.Ref<HTMLSpanElement>;
}

const RatingContainer = styled.span<DisplayProps>`
  ${display};
`;

const StarContainer = styled('span')<DisplayProps & PositionProps & CursorProps>`
  ${cursor};
  ${display};
  ${position};
`;

const EmptySymbol = styled('span')<VisibilityProps>`
  ${visibility};
`;

const FullSymbolOverlay = styled('span')<
  DisplayProps & PositionProps & OverflowProps & TopProps & LeftProps & WidthProps
>`
  ${display};
  ${position};
  ${overflow};
  ${top};
  ${left};
  ${width};
`;

const fullSymbolOverlayStyles: ComponentProps<typeof FullSymbolOverlay> = {
  display: 'inline-block',
  position: 'absolute',
  overflow: 'hidden',
  top: 0,
  left: 0,
};
const ReactRating = ({
  initialRating = 0,
  readonly = false,
  onChange,
  emptySymbol = [],
  fullSymbol = [],
  className,
  ref,
}: ReactRatingProps) => {
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const empty = ([] as React.ReactNode[]).concat(emptySymbol);
  const full = ([] as React.ReactNode[]).concat(fullSymbol);
  const totalSymbols = Math.max(empty.length, full.length, 5);

  const renderedValue = hoverValue !== null ? hoverValue : initialRating;
  const fullCount = Math.floor(renderedValue);

  return (
    <RatingContainer
      ref={ref}
      className={className}
      display="inline-block"
      {...(!readonly ? { onMouseLeave: () => setHoverValue(null) } : {})}
    >
      {Array.from({ length: totalSymbols }, (_, i) => {
        let percent: number;
        if (i < fullCount) {
          percent = 100;
        } else if (i === fullCount) {
          percent = (renderedValue - i) * 100;
        } else {
          percent = 0;
        }

        return (
          <StarContainer
            key={i}
            display="inline-block"
            position="relative"
            cursor={!readonly ? 'pointer' : 'inherit'}
            {...(!readonly
              ? {
                  onClick: (e: React.MouseEvent) => {
                    e.preventDefault();
                    onChange?.(i + 1);
                  },
                  onMouseMove: () => setHoverValue(i + 1),
                }
              : {})}
          >
            <EmptySymbol visibility={percent >= 100 ? 'hidden' : 'visible'}>
              {empty[i % empty.length]}
            </EmptySymbol>
            <FullSymbolOverlay {...fullSymbolOverlayStyles} width={`${percent}%`}>
              {full[i % full.length]}
            </FullSymbolOverlay>
          </StarContainer>
        );
      })}
    </RatingContainer>
  );
};

export default ReactRating;
